"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.coverageActionBodySchema = exports.listPatientCoveragesQuerySchema = exports.createPatientCoverageSchema = exports.planRuleParamsSchema = exports.createPlanNetworkSchema = exports.createPlanWaitingPeriodSchema = exports.createPlanExclusionSchema = exports.listInsurancePlansQuerySchema = exports.updateInsurancePlanSchema = exports.createInsurancePlanSchema = exports.listInsurancePayersQuerySchema = exports.updateInsurancePayerSchema = exports.createInsurancePayerSchema = exports.insurancePayerTypeEnum = void 0;
const zod_1 = require("zod");
/**
 * INSURANCE MODULE, PHASE 1 (ADR-098) — the request schemas for the insurer,
 * plan and policy screens.
 *
 * MONEY IS INTEGER PAISE and percentages are whole numbers, exactly as the
 * engine stores them. A screen that shows rupees converts at the edge; nothing
 * on the wire is a float, because a float here becomes a claim that is a paisa
 * out on a thirty-day stay.
 */
const paise = zod_1.z.number().int().min(0).max(100_000_000_000);
const ipChargeGroup = zod_1.z.enum([
    'ROOM_RENT', 'NURSING', 'DOCTOR_VISIT', 'LABORATORY', 'RADIOLOGY', 'PROCEDURE',
    'OPERATION_THEATRE', 'PHARMACY', 'CONSUMABLE', 'BLOOD_BANK', 'PACKAGE', 'MISCELLANEOUS',
]);
/** Who can be an insurer here. SELF_PAY and CORPORATE have their own screens. */
exports.insurancePayerTypeEnum = zod_1.z.enum(['INSURANCE', 'TPA', 'GOVERNMENT_SCHEME']);
const code = (label) => zod_1.z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(zod_1.z.string().regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, `${label} must be 2-30 letters, digits, dash or underscore.`));
// ---------------------------------------------------------------------------
// Insurers and TPAs — rows of `mst_payers`, with the type pinned.
// ---------------------------------------------------------------------------
exports.createInsurancePayerSchema = zod_1.z.object({
    payerCode: code('Payer code'),
    name: zod_1.z.string().trim().min(2).max(255),
    payerType: exports.insurancePayerTypeEnum,
    contactEmail: zod_1.z.string().trim().email().max(255).optional(),
    contactPhone: zod_1.z.string().trim().min(5).max(20).optional(),
});
/** Code and type are immutable: invoices, plans and policies already point at them. */
exports.updateInsurancePayerSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2).max(255).optional(),
    contactEmail: zod_1.z.string().trim().email().max(255).nullable().optional(),
    contactPhone: zod_1.z.string().trim().min(5).max(20).nullable().optional(),
    isActive: zod_1.z.boolean().optional(),
});
exports.listInsurancePayersQuerySchema = zod_1.z.object({
    q: zod_1.z.string().trim().max(100).optional(),
    payerType: exports.insurancePayerTypeEnum.optional(),
    includeInactive: zod_1.z.enum(['true', 'false']).optional(),
});
// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------
exports.createInsurancePlanSchema = zod_1.z
    .object({
    payerId: zod_1.z.string().uuid(),
    planCode: code('Plan code'),
    name: zod_1.z.string().trim().min(2).max(255),
    defaultSumInsured: paise.nullable().optional(),
    /** Percent of every covered amount the patient bears, 0-100. */
    copayPercent: zod_1.z.number().int().min(0).max(100).default(0),
    /** Fixed amount the patient bears per stay before the policy pays. */
    deductibleAmount: paise.default(0),
    /** Per-day room-rent ceiling. Null means no cap. */
    roomRentCapPerDay: paise.nullable().optional(),
    allowsNonNetwork: zod_1.z.boolean().default(false),
    requiresPreAuth: zod_1.z.boolean().default(true),
    effectiveFrom: zod_1.z.coerce.date(),
    effectiveTo: zod_1.z.coerce.date().nullable().optional(),
})
    .refine((v) => !v.effectiveTo || v.effectiveTo > v.effectiveFrom, {
    message: 'effectiveTo must be after effectiveFrom.',
    path: ['effectiveTo'],
});
/** Insurer and plan code are fixed once created: policies, claims and ledgers reference them. */
exports.updateInsurancePlanSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2).max(255).optional(),
    defaultSumInsured: paise.nullable().optional(),
    copayPercent: zod_1.z.number().int().min(0).max(100).optional(),
    deductibleAmount: paise.optional(),
    roomRentCapPerDay: paise.nullable().optional(),
    allowsNonNetwork: zod_1.z.boolean().optional(),
    requiresPreAuth: zod_1.z.boolean().optional(),
    effectiveFrom: zod_1.z.coerce.date().optional(),
    effectiveTo: zod_1.z.coerce.date().nullable().optional(),
    isActive: zod_1.z.boolean().optional(),
});
exports.listInsurancePlansQuerySchema = zod_1.z.object({
    q: zod_1.z.string().trim().max(100).optional(),
    payerId: zod_1.z.string().uuid().optional(),
    includeInactive: zod_1.z.enum(['true', 'false']).optional(),
    page: zod_1.z.coerce.number().int().min(1).optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).optional(),
});
// ---------------------------------------------------------------------------
// Plan rules: exclusion, waiting period, network. A rule names ONE service or
// ONE charge group — never both, never neither.
// ---------------------------------------------------------------------------
const oneTarget = (v) => (v.chargeId !== undefined) !== (v.chargeGroup !== undefined);
const oneTargetMessage = { message: 'Name either a service or a charge group, not both and not neither.' };
exports.createPlanExclusionSchema = zod_1.z
    .object({
    chargeId: zod_1.z.string().uuid().optional(),
    chargeGroup: ipChargeGroup.optional(),
    reason: zod_1.z.string().trim().max(500).optional(),
})
    .refine(oneTarget, oneTargetMessage);
exports.createPlanWaitingPeriodSchema = zod_1.z
    .object({
    chargeId: zod_1.z.string().uuid().optional(),
    chargeGroup: ipChargeGroup.optional(),
    waitingDays: zod_1.z.number().int().min(1).max(3650),
    description: zod_1.z.string().trim().max(500).optional(),
})
    .refine(oneTarget, oneTargetMessage);
exports.createPlanNetworkSchema = zod_1.z
    .object({
    hospitalId: zod_1.z.string().uuid().optional(),
    branchId: zod_1.z.string().uuid().optional(),
    isNetwork: zod_1.z.boolean().default(true),
    empanelmentNumber: zod_1.z.string().trim().max(60).optional(),
    effectiveFrom: zod_1.z.coerce.date(),
    effectiveTo: zod_1.z.coerce.date().nullable().optional(),
})
    .refine((v) => !v.effectiveTo || v.effectiveTo > v.effectiveFrom, {
    message: 'effectiveTo must be after effectiveFrom.',
    path: ['effectiveTo'],
});
exports.planRuleParamsSchema = zod_1.z.object({ id: zod_1.z.string().uuid(), ruleId: zod_1.z.string().uuid() });
// ---------------------------------------------------------------------------
// Patient policies
// ---------------------------------------------------------------------------
exports.createPatientCoverageSchema = zod_1.z
    .object({
    patientId: zod_1.z.string().uuid(),
    payerId: zod_1.z.string().uuid(),
    /** The TPA that administers the policy, when the insurer uses one. */
    administeredByPayerId: zod_1.z.string().uuid().optional(),
    planId: zod_1.z.string().uuid().optional(),
    policyNumber: zod_1.z.string().trim().min(3).max(60),
    groupNumber: zod_1.z.string().trim().max(100).optional(),
    relationshipToSubscriberId: zod_1.z.string().uuid(),
    coveragePriority: zod_1.z.enum(['PRIMARY', 'SECONDARY', 'TERTIARY']).default('PRIMARY'),
    cashlessEligible: zod_1.z.boolean().optional(),
    preAuthRequired: zod_1.z.boolean().optional(),
    /** Sum insured printed on this policy, in paise. */
    sumInsuredAmount: paise.optional(),
    validFrom: zod_1.z.coerce.date().optional(),
    validTo: zod_1.z.coerce.date().optional(),
})
    .refine((v) => !v.validTo || !v.validFrom || v.validTo > v.validFrom, {
    message: 'validTo must be after validFrom.',
    path: ['validTo'],
});
exports.listPatientCoveragesQuerySchema = zod_1.z.object({ patientId: zod_1.z.string().uuid() });
exports.coverageActionBodySchema = zod_1.z.object({ expectedLockVersion: zod_1.z.number().int().nonnegative() });
//# sourceMappingURL=insurance-masters.schema.js.map